import { Pressable } from 'react-native';
import { Text, XStack } from 'tamagui';
import { useThemeColors, Icon } from '@kiips/ui';
import type { Journey } from '@my-fullstack-app/itinerary-types';
import { journeyModeIcon, journeyModeLabel } from '../../lib/journey-format';

/** Every mode a Journey can take, in the order they're offered. */
export const JOURNEY_MODES: Journey['mode'][] = [
  'flight',
  'train',
  'bus',
  'ferry',
  'car-rental',
  'transfer',
];

interface JourneyModeChipsProps {
  value: Journey['mode'];
  onChange: (mode: Journey['mode']) => void;
  /** When false the chips render inert (read-only trip roles). */
  canEdit?: boolean | undefined;
}

/**
 * Mode picker shared by the add sheet and the journey detail screen —
 * the two places a journey's mode can be set. Previously duplicated
 * verbatim in both, including the `MODES` array.
 */
export function JourneyModeChips({ value, onChange, canEdit = true }: JourneyModeChipsProps) {
  const theme = useThemeColors();
  return (
    <XStack flexWrap="wrap" gap={8}>
      {JOURNEY_MODES.map((m) => {
        const selected = value === m;
        return (
          <Pressable
            key={m}
            onPress={() => canEdit && onChange(m)}
            disabled={!canEdit}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={journeyModeLabel(m)}
          >
            <XStack
              paddingHorizontal={12}
              paddingVertical={8}
              borderRadius={18}
              backgroundColor={selected ? '$accentCerulean' : '$surface'}
              borderWidth={1}
              borderColor={selected ? '$accentCerulean' : '$borderDefault'}
              alignItems="center"
              gap={6}
            >
              <Icon
                name={journeyModeIcon(m)}
                size={14}
                color={selected ? theme.textInverse : theme.textOnSurface}
              />
              <Text
                fontSize={12}
                fontWeight="700"
                color={selected ? '$textInverse' : '$textOnSurface'}
              >
                {journeyModeLabel(m)}
              </Text>
            </XStack>
          </Pressable>
        );
      })}
    </XStack>
  );
}
